/**
 * Edit a workflow's metadata: name, description, tags.
 *
 * Direction + canonical model/version are intentionally NOT editable —
 * they shape the runtime contract (which workflows can be chained,
 * which canonical the editor builds against, etc.). Changing them
 * would invalidate every existing version's definition, so we treat
 * them as immutable: to change direction or canonical, clone the
 * workflow into a fresh one.
 *
 * Lives next to WorkflowCreateDialog so the two stay visually
 * consistent — same field order, same validation, same hint copy.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { TagsField } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  workflowsApi,
  type WorkflowDetail,
} from "@/lib/api-workflows";


export interface WorkflowEditMetadataDialogProps {
  open: boolean;
  workflow: WorkflowDetail;
  onOpenChange: (open: boolean) => void;
  /** ADR-0065 T1-4 — the on-failure handler is a DEFINITION field (not a row
   *  column), so it's owned by the editor and persisted on the editor Save.
   *  The dialog is a controlled surface for it: value + change callback. */
  errorWorkflowId?: string | null;
  onErrorWorkflowIdChange?: (id: string | null) => void;
}


export function WorkflowEditMetadataDialog({
  open,
  workflow,
  onOpenChange,
  errorWorkflowId = null,
  onErrorWorkflowIdChange,
}: WorkflowEditMetadataDialogProps) {
  const qc = useQueryClient();
  const [name, setName] = React.useState(workflow.name);
  const [description, setDescription] = React.useState(workflow.description ?? "");
  const [tags, setTags] = React.useState<string[]>(workflow.tags ?? []);

  // Reset state every time the dialog opens — defends against stale
  // values when the same dialog instance is re-opened after an edit
  // somewhere else (which would otherwise show last-typed-but-not-saved).
  React.useEffect(() => {
    if (!open) return;
    setName(workflow.name);
    setDescription(workflow.description ?? "");
    setTags(workflow.tags ?? []);
  }, [open, workflow]);

  // Candidate handlers for the on-failure picker — every OTHER workflow
  // (a self-reference is harmless thanks to the loop guard, but we hide it
  // to steer toward a dedicated handler). Only fetched while the dialog is
  // open and the editor wired the change callback.
  const handlersEnabled = open && !!onErrorWorkflowIdChange;
  const handlersQuery = useQuery({
    queryKey: ["workflows", "error-handler-options"],
    // ADR-0065 T1-4 — n8n parity: only a workflow that CONTAINS an Error Trigger
    // node is a valid on-failure handler, so the picker lists exactly those.
    queryFn: () => workflowsApi.list({ has_error_trigger: true, page_size: 100 }),
    enabled: handlersEnabled,
    staleTime: 60_000,
  });
  const handlerOptions = React.useMemo(
    () =>
      (handlersQuery.data?.items ?? [])
        .filter((w) => w.id !== workflow.id && !w.is_subflow)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [handlersQuery.data, workflow.id],
  );

  const submit = useMutation<unknown, Error>({
    mutationFn: async () => {
      await workflowsApi.update(workflow.id, {
        name: name.trim(),
        description: description.trim() || null,
        tags,
      });
    },
    onSuccess: () => {
      // Invalidate both the list AND this workflow's detail so the
      // editor header reflects the new name/description immediately.
      qc.invalidateQueries({ queryKey: ["workflows"] });
      qc.invalidateQueries({ queryKey: ["workflow", workflow.id] });
      onOpenChange(false);
    },
  });

  const canSubmit = name.trim().length > 0 && !submit.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            Edit workflow
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-1.5">
            <Label htmlFor="wf-edit-name">Name</Label>
            <Input
              id="wf-edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wf-edit-desc">Description</Label>
            <Input
              id="wf-edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this workflow do?"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wf-edit-tags">Tags</Label>
            <TagsField id="wf-edit-tags" value={tags} onChange={setTags} />
            <p className="text-[11px] text-muted-foreground">
              Used for filtering in lists and as the target for alert rules
              with <span className="font-mono">target_kind=tag</span>.
            </p>
          </div>

          {/* On-failure handler (ADR-0065 T1-4). A DEFINITION field, so it's
              persisted with the workflow's Save, not this dialog's row-save. */}
          {onErrorWorkflowIdChange && (
            <div className="space-y-1.5">
              <Label htmlFor="wf-edit-error-wf">Workflow di errore</Label>
              <select
                id="wf-edit-error-wf"
                className="flex h-9 w-full rounded-md border border-border bg-surface px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={errorWorkflowId ?? ""}
                onChange={(e) =>
                  onErrorWorkflowIdChange(e.target.value || null)
                }
              >
                <option value="">Nessuno</option>
                {handlerOptions.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">
                Se un'esecuzione in produzione fallisce, questo workflow viene
                lanciato con il contesto dell'errore (chiave{" "}
                <span className="font-mono">$error</span>). Si applica al{" "}
                <strong>salvataggio del workflow</strong>, non a questo dialogo.
              </p>
            </div>
          )}

          {/* Immutable section — surfaced so the user knows WHY they can't
              change these here, and what to do if they need to. */}
          <div className="space-y-1.5 border-t border-border/60 pt-3">
            <Label className="text-muted-foreground">Immutable</Label>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge tone="neutral">{workflow.direction}</Badge>
              <span className="font-mono text-muted-foreground">
                {workflow.canonical_model}.v{workflow.canonical_version}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Direction and canonical binding are part of the workflow's
              runtime contract. To change them, clone this workflow into a
              fresh one with the new settings.
            </p>
          </div>

          {submit.error && (
            <Alert tone="danger" title="Save failed">
              {(submit.error as Error).message}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit}
            onClick={() => submit.mutate()}
          >
            {submit.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
            )}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
